// Leaflet-powered map view — drop-in alternative to MapView.jsx (Google Maps).
// Same public API/props so App.js can swap engines via env var without any
// other changes. Uses CartoDB Voyager tiles (free, no API key) and
// leaflet.markercluster for the same clustering UX.
import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { AlertCircle } from "lucide-react";
import { categoryFor } from "../lib/categories";
import { CATEGORY_PATHS, CATEGORY_STROKE } from "../lib/categoryIcons";
import { faviconFor } from "../lib/api";

// ----- Same pin SVG helpers ported 1:1 from the Google Maps version. -----
function categoryPinSvg(catId) {
    const stroke = CATEGORY_STROKE[catId] || CATEGORY_STROKE._default;
    const path = CATEGORY_PATHS[catId] || CATEGORY_PATHS._default;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="56" viewBox="0 0 46 56">
<defs><filter id="d" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="${stroke}" flood-opacity="0.35"/></filter></defs>
<g filter="url(#d)">
<polygon points="23,54 15,44 31,44" fill="${stroke}"/>
<circle cx="23" cy="21" r="19" fill="#ffffff" stroke="${stroke}" stroke-width="3"/>
<g transform="translate(11,9)" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none">${path}</g>
</g></svg>`;
}

function logoPinSvg(catId, logoUrl) {
    const stroke = CATEGORY_STROKE[catId] || CATEGORY_STROKE._default;
    const path = CATEGORY_PATHS[catId] || CATEGORY_PATHS._default;
    const safeUrl = (logoUrl || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    return `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="56" viewBox="0 0 46 56">
<defs>
<filter id="d" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="${stroke}" flood-opacity="0.35"/>
</filter>
<clipPath id="lc"><circle cx="23" cy="21" r="14"/></clipPath>
</defs>
<g filter="url(#d)">
<polygon points="23,54 15,44 31,44" fill="${stroke}"/>
<circle cx="23" cy="21" r="19" fill="#ffffff" stroke="${stroke}" stroke-width="3"/>
<g transform="translate(11,9)" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none" opacity="0.35">${path}</g>
<image href="${safeUrl}" x="9" y="7" width="28" height="28" clip-path="url(#lc)" preserveAspectRatio="xMidYMid slice"/>
</g></svg>`;
}

function employerPinSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="54" height="64" viewBox="0 0 54 64">
<defs><filter id="d2" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="#a5340a" flood-opacity="0.55"/></filter>
<radialGradient id="og" cx="50%" cy="30%" r="70%">
<stop offset="0%" stop-color="#ffb488"/><stop offset="55%" stop-color="#f47c3c"/><stop offset="100%" stop-color="#d94b08"/>
</radialGradient></defs>
<g filter="url(#d2)">
<polygon points="27,62 18,50 36,50" fill="#d94b08"/>
<circle cx="27" cy="25" r="22" fill="url(#og)" stroke="#ffffff" stroke-width="3"/>
<polygon points="27,12 30.2,20.6 39.4,20.9 32.1,26.5 34.6,35.4 27,30.4 19.4,35.4 21.9,26.5 14.6,20.9 23.8,20.6"
 fill="#ffffff" opacity="0.98"/>
</g></svg>`;
}

function featuredGoldPinSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="58" height="70" viewBox="0 0 58 70">
<defs><filter id="d3" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="5" stdDeviation="3.5" flood-color="#8a6a06" flood-opacity="0.6"/></filter>
<radialGradient id="gg" cx="50%" cy="25%" r="75%">
<stop offset="0%" stop-color="#fff3b8"/><stop offset="45%" stop-color="#f5c542"/><stop offset="100%" stop-color="#b8890c"/>
</radialGradient>
<linearGradient id="gring" x1="0%" y1="0%" x2="0%" y2="100%">
<stop offset="0%" stop-color="#ffffff"/><stop offset="100%" stop-color="#fff2c4"/>
</linearGradient>
<linearGradient id="gshine" x1="0%" y1="0%" x2="100%" y2="0%">
<stop offset="0%" stop-color="#fff9dd" stop-opacity="0"/>
<stop offset="50%" stop-color="#ffffff" stop-opacity="0.85"/>
<stop offset="100%" stop-color="#fff9dd" stop-opacity="0"/>
</linearGradient>
<clipPath id="gclip"><circle cx="29" cy="27" r="24"/></clipPath></defs>
<g filter="url(#d3)">
<polygon points="29,68 19,55 39,55" fill="#b8890c"/>
<circle cx="29" cy="27" r="26" fill="none" stroke="#f5c542" stroke-width="2" opacity="0.55">
  <animate attributeName="r" values="24;30;24" dur="2.4s" repeatCount="indefinite"/>
  <animate attributeName="opacity" values="0.6;0;0.6" dur="2.4s" repeatCount="indefinite"/>
</circle>
<circle cx="29" cy="27" r="24" fill="url(#gg)" stroke="url(#gring)" stroke-width="3.5"/>
<g clip-path="url(#gclip)">
  <rect x="-30" y="3" width="20" height="48" fill="url(#gshine)" opacity="0.9" transform="skewX(-18)">
    <animate attributeName="x" values="-30;70;70" keyTimes="0;0.55;1" dur="3.2s" repeatCount="indefinite"/>
  </rect>
</g>
<polygon points="29,13 32.5,22.4 42.6,22.7 34.6,29 37.4,38.8 29,33 20.6,38.8 23.4,29 15.4,22.7 25.5,22.4"
 fill="#ffffff" opacity="0.98" stroke="#a5340a" stroke-width="0.4"/>
</g></svg>`;
}

const _iconCache = {};

// Deterministic 0-100 hash from job.id — used to (a) decide whether a marker
// breathes and (b) stagger its animation-delay so a wave of pins pulse
// independently instead of in lockstep.
function _idHash(id) {
    let h = 0;
    const s = String(id || "");
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h) % 100;
}

function iconFor(job) {
    let key, w, h, ax, ay, svg;
    if (job.featured) {
        key = "__gold"; w = 58; h = 70; ax = 29; ay = 68;
        svg = featuredGoldPinSvg();
    } else if (job.src === "employer") {
        key = "__emp"; w = 54; h = 64; ax = 27; ay = 62;
        svg = employerPinSvg();
    } else {
        const catId = categoryFor(job.cat).id;
        const logo = job.logo_url || faviconFor(job.company);
        w = 46; h = 56; ax = 23; ay = 54;
        if (logo) {
            key = `l-${catId}-${logo}`;
            svg = logoPinSvg(catId, logo);
        } else {
            key = `c-${catId}`;
            svg = categoryPinSvg(catId);
        }
    }

    // Breathing rules — draw the eye to a subset of pins so users feel invited
    // to click. Featured (gold) + verified employer pins ALWAYS breathe.
    // Regular pins breathe ~15% of the time, staggered so the map never pulses
    // in unison. Delay range 0-6s at 2.8s cycle → roughly 2 pulses per cycle,
    // desynchronised across the map.
    const hash = _idHash(job.id);
    const shouldBreathe = job.featured || job.src === "employer" || hash < 15;

    if (shouldBreathe) {
        const delay = ((hash * 137) % 600) / 100; // 0.00 – 5.99 s
        // Wrap the pin SVG in a breathing div. Unique per-marker so no caching.
        const wrapped = `<div class="dwj-breathe" style="animation-delay:${delay}s">${svg}</div>`;
        return L.divIcon({
            className: "dwj-leaflet-marker",
            html: wrapped,
            iconSize: [w, h],
            iconAnchor: [ax, ay],
            popupAnchor: [0, -ay + 2],
        });
    }

    if (_iconCache[key]) return _iconCache[key];
    _iconCache[key] = L.divIcon({
        className: "dwj-leaflet-marker",
        html: svg,
        iconSize: [w, h],
        iconAnchor: [ax, ay],
        popupAnchor: [0, -ay + 2],
    });
    return _iconCache[key];
}

function escapeHtml(s) {
    return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function popupHtml(job) {
    const cat = categoryFor(job.cat);
    const stroke = CATEGORY_STROKE[cat.id] || CATEGORY_STROKE._default;
    const isEmp = job.src === "employer";
    const isGold = !!job.featured;
    const iconBg = isGold ? "#fff5cf" : isEmp ? "#ffdccb" : cat.color;
    const iconStroke = isGold ? "#b8890c" : isEmp ? "#e85812" : stroke;
    const logo = !isGold && !isEmp ? (job.logo_url || faviconFor(job.company)) : "";
    const iconInner = logo
        ? `<img src="${escapeHtml(logo)}" alt="" style="width:22px;height:22px;border-radius:6px;object-fit:cover" onerror="this.style.display='none';this.nextSibling&&(this.nextSibling.style.display='')"/><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`
        : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`;
    return `<div style="min-width:220px;font-family:'DM Sans',sans-serif;padding:2px">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
        <span style="width:30px;height:30px;border-radius:8px;background:${iconBg};display:inline-flex;align-items:center;justify-content:center;overflow:hidden">${iconInner}</span>
        <strong style="font-family:'Outfit',sans-serif;color:#333A56;font-size:15px;line-height:1.15">${escapeHtml(job.title)}</strong>
      </div>
      ${isGold ? `<div style="display:inline-flex;align-items:center;gap:4px;background:linear-gradient(90deg,#fff3b8,#f5c542);color:#7a5806;font-size:11px;font-weight:800;padding:2px 9px;border-radius:999px;margin-bottom:4px;font-family:'Outfit',sans-serif">★ FEATURED</div>` : isEmp ? `<div style="display:inline-flex;align-items:center;gap:4px;background:#fff2ea;color:#e85812;font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px;margin-bottom:4px;font-family:'Outfit',sans-serif">★ FEATURED EMPLOYER</div>` : ""}
      <div style="color:#4a536f;font-size:13px;margin-left:2px">${escapeHtml(job.company)}</div>
      <div style="color:#6b7080;font-size:12px;margin-top:2px;margin-left:2px">${escapeHtml(job.loc || job.city)}</div>
      ${job.pay ? `<div style="color:#e85812;font-weight:700;font-size:13px;margin-top:6px;font-family:'Outfit',sans-serif;margin-left:2px">${escapeHtml(job.pay)}</div>` : ""}
      <button data-jid="${escapeHtml(job.id)}" class="dwj-popup-apply" style="margin-top:10px;background:#e85812;color:#fff;border:none;padding:9px 14px;border-radius:999px;font-weight:600;font-family:'Outfit',sans-serif;cursor:pointer;width:100%;font-size:13px">1-tap Apply →</button>
    </div>`;
}

// Cluster bubble renderer — matches the Google Maps look (orange badge with count).
function clusterIcon(cluster) {
    const count = cluster.getChildCount();
    const size = count < 10 ? 34 : count < 50 ? 40 : count < 200 ? 48 : 56;
    // Bigger clusters (10+) breathe to guide the eye toward job-dense areas.
    // Delay derived from count so neighbouring clusters pulse out of sync.
    const breathe = count >= 10;
    const delay = ((count * 173) % 550) / 100; // 0-5.5s
    const wrapClass = breathe ? "dwj-breathe" : "";
    const wrapStyle = breathe ? `animation-delay:${delay}s;` : "";
    return L.divIcon({
        className: "dwj-cluster-icon",
        html: `<div class="${wrapClass}" style="${wrapStyle}width:${size}px;height:${size}px;border-radius:50%;background:radial-gradient(circle at 30% 30%,#ffb488,#e85812);color:#fff;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:800;font-size:${count < 100 ? 14 : 12}px;box-shadow:0 4px 12px rgba(232,88,18,.45);border:3px solid #fff">${count}</div>`,
        iconSize: [size, size],
    });
}

// Basemap tiles — CartoDB now requires an API key and watermarks unlicensed
// requests with "API KEY REQUIRED". We ship Esri's keyless World Light Gray
// Canvas as the default (matches the previous Carto Positron aesthetic — clean
// grey/white, minimal colour) and fall back to Carto only if
// REACT_APP_CARTO_KEY is provided.
const CARTO_KEY = (process.env.REACT_APP_CARTO_KEY || "").trim();
// Esri light gray canvas — free, keyless, retina-quality, matches the app's
// muted aesthetic. Attribution required per Esri terms.
const ESRI_TILES =
    "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTR =
    '&copy; <a href="https://www.esri.com/">Esri</a>, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const CARTO_TILES = CARTO_KEY
    ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?api_key=${encodeURIComponent(CARTO_KEY)}`
    : "";
const CARTO_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';
const BASEMAP_URL = CARTO_TILES || ESRI_TILES;
const BASEMAP_ATTR = CARTO_TILES ? CARTO_ATTR : ESRI_ATTR;
const BASEMAP_SUBDOMAINS = CARTO_TILES ? "abcd" : "";
// USA + Canada bounding box (incl. Alaska, Hawaii, Puerto Rico, all Canadian territories).
const NA_BOUNDS = [[15.0, -172.0], [72.0, -50.0]];
const DEFAULT_BOUNDS = [[24.5, -125.0], [55.0, -66.0]]; // continental fit for initial view

// Popup padding — pushes the map view so popups never open under the fixed header/filter chips.
// Filter chips row measured bottom = 168px on desktop, so we bump padding to 190 for safety.
const POPUP_AUTOPAN_TOP = 190;

export default function MapViewLeaflet({ jobs, center, zoom, onJobClick, onBoundsChange, focusJobId }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const clusterRef = useRef(null);
    const markersByIdRef = useRef({});
    const jobsRef = useRef(jobs);
    const onJobClickRef = useRef(onJobClick);
    const [loadErr, setLoadErr] = useState("");
    const [mapReady, setMapReady] = useState(false);
    const [mapVersion, setMapVersion] = useState(0);

    useEffect(() => { jobsRef.current = jobs; }, [jobs]);
    useEffect(() => { onJobClickRef.current = onJobClick; }, [onJobClick]);

    // ---- Map init ----
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;
        try {
            const map = L.map(containerRef.current, {
                zoomControl: true,
                worldCopyJump: false,
                preferCanvas: false,
                minZoom: 3,
                maxZoom: 18,
                // Lock panning + zooming to the USA & Canada region only.
                maxBounds: NA_BOUNDS,
                maxBoundsViscosity: 1.0,
            });
            if (center && zoom) {
                map.setView([center[0], center[1]], zoom);
            } else {
                map.fitBounds(DEFAULT_BOUNDS);
            }
            const tileOpts = {
                attribution: BASEMAP_ATTR,
                maxZoom: 18,
                detectRetina: true,
            };
            if (BASEMAP_SUBDOMAINS) tileOpts.subdomains = BASEMAP_SUBDOMAINS;
            L.tileLayer(BASEMAP_URL, tileOpts).addTo(map);

            const cluster = L.markerClusterGroup({
                showCoverageOnHover: false,
                spiderfyOnMaxZoom: true,
                maxClusterRadius: 55,
                disableClusteringAtZoom: 17,
                iconCreateFunction: clusterIcon,
            });
            cluster.on("clusterclick", (e) => {
                const zoomNow = map.getZoom();
                if (zoomNow >= 15) {
                    // At high zoom, prefer to show a list popup over further zoom-in.
                    const kids = e.layer.getAllChildMarkers();
                    const clusterJobs = kids.map((m) => m._dwjJob).filter(Boolean);
                    if (clusterJobs.length) {
                        L.popup({
                            maxWidth: 300,
                            autoPan: true,
                            autoPanPaddingTopLeft: L.point(20, POPUP_AUTOPAN_TOP),
                            autoPanPaddingBottomRight: L.point(20, 60),
                        })
                            .setLatLng(e.latlng)
                            .setContent(clusterListHtml(clusterJobs))
                            .openOn(map);
                        setTimeout(() => wireClusterListClicks(map), 50);
                    }
                }
            });
            cluster.addTo(map);

            mapRef.current = map;
            clusterRef.current = cluster;

            let boundsTimer = null;
            const emit = () => {
                try {
                    const b = map.getBounds();
                    if (!b) return;
                    const sw = b.getSouthWest();
                    const ne = b.getNorthEast();
                    onBoundsChange && onBoundsChange({
                        contains: ([lat, lng]) => b.contains([lat, lng]),
                        bbox: { south: sw.lat, west: sw.lng, north: ne.lat, east: ne.lng },
                    });
                } catch { /* ignore */ }
            };
            map.on("moveend zoomend", () => {
                clearTimeout(boundsTimer);
                boundsTimer = setTimeout(() => {
                    emit();
                    setMapVersion((v) => v + 1);
                }, 120);
            });
            setTimeout(emit, 250);

            setMapReady(true);
        } catch (e) {
            console.error("Leaflet init error:", e);
            setLoadErr(String(e.message || e));
        }
        return () => {
            if (mapRef.current) {
                try { mapRef.current.remove(); } catch { /* ignore */ }
                mapRef.current = null;
            }
            clusterRef.current = null;
            markersByIdRef.current = {};
        };
    }, []);

    // ---- Sync markers to viewport (with padding buffer, threshold + diff) ----
    useEffect(() => {
        if (!mapReady || !mapRef.current || !clusterRef.current) return;
        const map = mapRef.current;
        const cluster = clusterRef.current;

        const VIEWPORT_MARKER_THRESHOLD = 300;
        let activeJobs = jobs;
        if (jobs.length > VIEWPORT_MARKER_THRESHOLD) {
            const b = map.getBounds();
            if (b) {
                const n = b.getNorth(), s = b.getSouth();
                const e = b.getEast(), w = b.getWest();
                const latPad = (n - s) * 0.5;
                const lngPad = (e - w) * 0.5;
                activeJobs = jobs.filter((j) => {
                    if (j.lat == null || j.lng == null) return false;
                    return j.lat >= s - latPad && j.lat <= n + latPad
                        && j.lng >= w - lngPad && j.lng <= e + lngPad;
                });
            }
        }

        // Diff: remove markers no longer active, add new ones.
        const currentIds = new Set(activeJobs.map((j) => j.id));
        const prevIds = Object.keys(markersByIdRef.current);
        const toRemove = [];
        for (const id of prevIds) {
            if (!currentIds.has(id)) {
                toRemove.push(markersByIdRef.current[id]);
                delete markersByIdRef.current[id];
            }
        }
        if (toRemove.length) cluster.removeLayers(toRemove);

        const newJobs = [];
        for (const j of activeJobs) {
            if (j.lat == null || j.lng == null) continue;
            if (markersByIdRef.current[j.id]) continue;
            newJobs.push(j);
        }

        // Chunk-add with rAF to keep UI responsive.
        let cancelled = false;
        const CHUNK = 300;
        let idx = 0;
        const build = () => {
            if (cancelled) return;
            const end = Math.min(idx + CHUNK, newJobs.length);
            const batch = [];
            for (; idx < end; idx++) {
                const j = newJobs[idx];
                const m = L.marker([j.lat, j.lng], {
                    icon: iconFor(j),
                    title: `${j.title} — ${j.company}`,
                    riseOnHover: true,
                    zIndexOffset: j.featured ? 1000 : j.src === "employer" ? 500 : 0,
                });
                m._dwjJob = j;
                m.on("click", (ev) => {
                    L.DomEvent.stopPropagation(ev);
                    const cur = (jobsRef.current || []).find((x) => x.id === j.id) || j;
                    L.popup({
                        maxWidth: 260,
                        offset: [0, -2],
                        autoPan: true,
                        // Keep the popup clear of the fixed header + filter chips at the top.
                        autoPanPaddingTopLeft: L.point(20, POPUP_AUTOPAN_TOP),
                        autoPanPaddingBottomRight: L.point(20, 60),
                    })
                        .setLatLng([cur.lat, cur.lng])
                        .setContent(popupHtml(cur))
                        .openOn(map);
                    setTimeout(() => {
                        const btn = document.querySelector(".dwj-popup-apply");
                        if (btn) {
                            btn.onclick = (e2) => {
                                e2.preventDefault();
                                const jid = btn.getAttribute("data-jid");
                                const job = (jobsRef.current || []).find((x) => x.id === jid) || cur;
                                if (onJobClickRef.current) onJobClickRef.current(job);
                            };
                        }
                    }, 60);
                });
                markersByIdRef.current[j.id] = m;
                batch.push(m);
            }
            if (batch.length) cluster.addLayers(batch);
            if (idx < newJobs.length) requestAnimationFrame(build);
        };
        if (newJobs.length) requestAnimationFrame(build);

        return () => { cancelled = true; };
    }, [jobs, mapReady, mapVersion]);

    // ---- Focus a specific job (deep-link) ----
    useEffect(() => {
        if (!focusJobId || !mapRef.current) return;
        const job = (jobs || []).find((j) => j.id === focusJobId);
        if (!job || job.lat == null) return;
        mapRef.current.flyTo([job.lat, job.lng], Math.max(mapRef.current.getZoom(), 10), { duration: 0.6 });
        setTimeout(() => {
            const m = markersByIdRef.current[job.id];
            if (m) m.fire("click");
        }, 700);
    }, [focusJobId, jobs]);

    // ---- Recenter on center/zoom prop change ----
    useEffect(() => {
        if (!mapReady || !mapRef.current || !center) return;
        mapRef.current.flyTo([center[0], center[1]], zoom || mapRef.current.getZoom(), { duration: 0.4 });
    }, [center, zoom, mapReady]);

    if (loadErr) {
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white text-center p-6">
                <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
                <div className="font-display font-semibold text-[#1a1e2c] mb-1">Map couldn&apos;t load</div>
                <div className="text-slate-500 text-sm max-w-md">{loadErr}</div>
            </div>
        );
    }

    return (
        <>
            <div
                ref={containerRef}
                data-testid="map-view"
                className="absolute inset-0"
                style={{ zIndex: 0 }}
            />
            <style>{`
                .leaflet-container { background:#eef4f7 !important; }
                .leaflet-popup-content-wrapper { border-radius:14px; padding:6px 4px; }
                .leaflet-popup-content { margin:8px 12px !important; }
                .leaflet-popup-tip { background:#fff; }
                .dwj-leaflet-marker { background:transparent !important; border:none !important; }
                .dwj-cluster-icon { background:transparent !important; border:none !important; }
                @keyframes dwj-breathe {
                    0%, 100% { transform: scale(1);    opacity: 1; }
                    50%      { transform: scale(1.12); opacity: 0.92; }
                }
                .dwj-breathe {
                    animation: dwj-breathe 2.8s ease-in-out infinite;
                    transform-origin: center bottom;
                    will-change: transform, opacity;
                }
                @media (prefers-reduced-motion: reduce) {
                    .dwj-breathe { animation: none !important; }
                }
            `}</style>
        </>
    );
}

// ---- Cluster popup list ----
function clusterListHtml(jobs) {
    const rank = (j) => (j.featured ? 0 : j.src === "employer" ? 1 : 2);
    const sorted = [...jobs].sort((a, b) => rank(a) - rank(b));
    const rows = sorted.slice(0, 20).map((j) => {
        const cat = categoryFor(j.cat);
        const stroke = CATEGORY_STROKE[cat.id] || CATEGORY_STROKE._default;
        const isEmp = j.src === "employer";
        const isGold = !!j.featured;
        const badge = isGold
            ? `<span style="background:linear-gradient(90deg,#fff3b8,#f5c542);color:#7a5806;font-size:9px;font-weight:800;padding:1px 6px;border-radius:6px;letter-spacing:.4px;text-transform:uppercase;margin-left:6px">★ Featured</span>`
            : isEmp
                ? `<span style="background:#e85812;color:#fff;font-size:9px;font-weight:700;padding:1px 5px;border-radius:6px;letter-spacing:.4px;text-transform:uppercase;margin-left:6px">★</span>`
                : "";
        const iconBg = isGold ? "#fff5cf" : isEmp ? "#ffdccb" : cat.color;
        const iconStroke = isGold ? "#b8890c" : isEmp ? "#e85812" : stroke;
        const rowLogo = !isGold && !isEmp ? (j.logo_url || faviconFor(j.company)) : "";
        const rowIcon = rowLogo
            ? `<img src="${escapeHtml(rowLogo)}" alt="" style="width:18px;height:18px;border-radius:5px;object-fit:cover" onerror="this.style.display='none';this.nextSibling&&(this.nextSibling.style.display='')"/><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`
            : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`;
        return `<div class="dwj-cluster-row" data-jid="${escapeHtml(j.id)}" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:10px;cursor:pointer;background:#fff;border:1px solid #f0f0f0;margin-bottom:6px">
          <span style="width:26px;height:26px;border-radius:8px;background:${iconBg};display:inline-flex;align-items:center;justify-content:center;shrink:0;overflow:hidden">${rowIcon}</span>
          <div style="min-width:0;flex:1">
            <div style="font-family:'Outfit',sans-serif;font-weight:700;color:#1a1e2c;font-size:13px;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(j.title)}${badge}</div>
            <div style="color:#6b7080;font-size:11.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(j.company)}${j.pay ? ` · <span style="color:#e85812;font-weight:700">${escapeHtml(j.pay)}</span>` : ""}</div>
          </div>
        </div>`;
    }).join("");
    const more = jobs.length > 20 ? `<div style="text-align:center;font-size:11.5px;color:#6b7080;margin-top:4px">+${jobs.length - 20} more — zoom in further to see them all</div>` : "";
    return `<div style="min-width:260px;max-width:320px;font-family:'DM Sans',sans-serif;padding:2px">
      <div style="font-family:'Outfit',sans-serif;font-weight:800;font-size:14px;color:#1a1e2c;margin-bottom:8px;display:flex;align-items:center;gap:6px">
        <span style="background:#fff2ea;color:#e85812;font-size:10px;padding:2px 7px;border-radius:999px;font-weight:700;text-transform:uppercase;letter-spacing:.5px">${jobs.length} jobs here</span>
        <span style="color:#6b7080;font-weight:500;font-size:12px">Tap one to apply</span>
      </div>
      <div style="max-height:280px;overflow-y:auto;padding-right:2px">${rows}${more}</div>
    </div>`;
}

function wireClusterListClicks(map) {
    document.querySelectorAll(".dwj-cluster-row").forEach((el) => {
        el.onmouseenter = () => { el.style.background = "#fff2ea"; };
        el.onmouseleave = () => { el.style.background = "#fff"; };
        el.onclick = () => {
            const jid = el.getAttribute("data-jid");
            const job = (window.__dwjJobsRef?.current || []).find((x) => x.id === jid);
            if (job) map.closePopup();
        };
    });
}
